
import type { PropType } from 'vue'
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue'

import IMask from 'imask'

// > legacy/validation <
import useValidationMessages from '@/common/composable/useValidationMessages'
import type { ErrorObject } from '@vuelidate/core'

export default defineComponent({
    props: {
        type: {
            type: String as PropType<'text' | 'password'>,
            default: 'text'
        },
        size: {
            type: String as PropType<'medium' | 'big'>,
            default: 'medium'
        },
        modelValue: {
            type: [String, Number, null] as PropType<string | number | null>,
            default: null
        },
        mask: {
            type: Object as PropType<any>,
            default: null
        },
        placeholder: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        validationErrors: {
            type: Array as PropType<ErrorObject[]>,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'update:unmaskedValue', 'mask-complete', 'input', 'change', 'focus', 'blur'],
    setup(props, { emit }) {
        const { getValidationMessage } = useValidationMessages()

        const isFocused = ref(false)
        const inputRef = ref<HTMLElement | null>(null)
        let inputMask: any | null = null

        const onFocus = (event: FocusEvent) => {
            isFocused.value = true
            emit('focus', event)
        }

        const onBlur = (event: FocusEvent) => {
            isFocused.value = false
            emit('blur', event)
        }

        const onInput = (event: Event) => {
            if (props.disabled || Boolean(props.mask)) return

            const input = event.target as HTMLInputElement
            emit('update:modelValue', input.value)
            emit('input', event)
        }

        const onChange = (event: Event) => {
            if (props.disabled || Boolean(props.mask)) return

            const input = event.target as HTMLInputElement
            emit('change', input.value)
        }

        const onMaskAccept = () => {
            if (props.disabled) return

            emit('update:modelValue', inputMask.value)
            emit('update:unmaskedValue', inputMask.unmaskedValue)
        }

        onMounted(() => {
            if (props.mask && props.mask) {
                inputMask = IMask(inputRef.value as HTMLInputElement, props.mask)
                inputMask.on('accept', onMaskAccept)
            }
        })

        onBeforeUnmount(() => {
            if (inputMask) {
                inputMask.off('accept', onMaskAccept)
                inputMask.destroy()
            }
        })

        watch(
            () => props.disabled,
            (isDisabled) => {
                if (isDisabled) {
                    isFocused.value = false
                }
            }
        )

        watch(
            () => props.modelValue,
            (newValue) => {
                if (inputMask) {
                    inputMask.value = newValue ? newValue.toString() : ''
                    inputMask.updateValue()
                }
            }
        )

        return {
            inputRef,
            isFocused,
            onInput,
            onChange,
            onFocus,
            onBlur,
            getValidationMessage
        }
    }
})
